import React, { useEffect, useState } from 'react';
import { fetchInputFieldPriceMap, logEvent } from '../util/apiUtils';
import { getSupportedBillingMethodNames } from '../BillingOptions';

const DisplayPricingChoice = ({
	displayPricingType,
	setDisplayPricingType,
	componentPriceMap,
	setComponentPriceMap,
	orderableItem,
	practitioner
}) => {
	const [PTPAYSupported, setPTPAYSupported] = useState(false);
	const [IIPSupported, setIIPSupported] = useState(false);

	const orderableItemId = orderableItem?.id;
	const gsdlNo = practitioner?.gsdlNo;

	// Clear priceMap and displayType when the orderable item changes
	useEffect(() => {
		setDisplayPricingType(null);
		setComponentPriceMap(null);
	}, [orderableItemId, setComponentPriceMap, setDisplayPricingType]);

	// Check which billing methods are supported for the client and orderableItem
	useEffect(() => {
		const supportedBilingMethods = getSupportedBillingMethodNames(practitioner, orderableItem).filter(
			(billingMethod) =>
				billingMethod === process.env.REACT_APP_BILLING_INSURANCE_VALUE ||
				billingMethod === process.env.REACT_APP_BILLING_NO_INSURANCE_VALUE
		);

		setPTPAYSupported(supportedBilingMethods.includes(process.env.REACT_APP_BILLING_NO_INSURANCE_VALUE));
		setIIPSupported(supportedBilingMethods.includes(process.env.REACT_APP_BILLING_INSURANCE_VALUE));
	}, [orderableItem, practitioner]);

	// Set the displayPricing value and fetch prices if needed.
	const handleDisplayPricingChange = (event) => {
		const newDisplayType = event.target.value;
		setDisplayPricingType(newDisplayType);

		if (!componentPriceMap) {
			setComponentPriceMap({ loading: true });

			fetchInputFieldPriceMap(orderableItemId, gsdlNo)
				.then((json) => {
					setComponentPriceMap(json);
				})
				.catch((e) => {
					setComponentPriceMap({ error: true });
				})
				.finally(() => {
					logEvent(
						`{} - "Display Pricing" clicked. omsOrderableItemId={}, gsdlNo={}, type={}`,
						'UI_ACTION_DISPLAY_PANEL_PRICING',
						orderableItemId,
						gsdlNo,
						newDisplayType
					);
				});
		}
	};

	if (!PTPAYSupported && !IIPSupported) {
		return null;
	}

	return (
		<div>
			<span className="me-2">Display Pricing:</span>
			{PTPAYSupported && (
				<div className="radio-inline">
					<label className="radio cursor-pointer" style={{ fontWeight: 'normal', margin: '0' }}>
						<input
							type="radio"
							className="cursor-pointer"
							name="displayPricing"
							value="PTPAY"
							checked={displayPricingType === 'PTPAY'}
							onChange={handleDisplayPricingChange}
						/>
						Patient Pay
					</label>
				</div>
			)}
			{IIPSupported && (
				<div className="radio-inline">
					<label className="radio cursor-pointer" style={{ fontWeight: 'normal', margin: '0' }}>
						<input
							type="radio"
							className="cursor-pointer"
							name="displayPricing"
							value="IIP"
							checked={displayPricingType === 'IIP'}
							onChange={handleDisplayPricingChange}
						/>
						Insurance IIP
					</label>
				</div>
			)}
		</div>
	);
};

/**
 * Display a component's price, or a spinner or error when appropriate.
 */
const ComponentPrice = ({ inputField, priceMap, priceType, calculateBasePrice, showError }) => {
	if (!priceMap || !priceType) {
		return null;
	}

	if (priceMap.loading) {
		return <i className="fa fa-spinner fa-spin fa-fw mr-1 float-end" aria-hidden="true"></i>;
	}

	if (showError && priceMap.error) {
		return <i className="fa fa-warning text-warning" title="There was an error loading prices"></i>;
	}

	let price = getComponentPrice({ priceType, priceMap, calculateBasePrice, inputFieldId: inputField.id });

	if (!price) {
		return null;
	}

	return (
		<>
			<strong className='float-end'>${price}{calculateBasePrice && ' (Base)'}</strong>			
		</>
	);
};

const getComponentPrice = ({ priceType, priceMap, calculateBasePrice, inputFieldId }) => {
	if (!priceMap || !priceType || priceMap.loading || priceMap.error) {
		return null;
	}

	let price = 0;

	/**
	 * Mixed Pricing orderable items need to display a "Base" fee for the profile and the add-on
	 * fee for each component.
	 * The base fee is calculated by substracting an item's addOn price from its main price.
	 */
	if (calculateBasePrice) {
		const priceTypeMap = priceMap[priceType];
		const componentWithAddOnPrice = Object.values(priceTypeMap).find((prices) => prices.price && prices.addOnPrice);
		if (componentWithAddOnPrice) {
			price = componentWithAddOnPrice.price - componentWithAddOnPrice.addOnPrice;
		}
	}

	if (!price) {
		const inputFieldPrices = priceMap[priceType]?.[inputFieldId];
		price = inputFieldPrices?.addOnPrice || inputFieldPrices?.price || 0;
	}

	/**
	 * ID 6587 is the GI Effects with Microbiomix profile. This orderable item has special logic
	 * where the Microbiomix add-on is not displayed and gets added behind the scenes. Add the
	 * Microbiomix price (ID 6700) to the profile price.
	 */
	if (inputFieldId === 6587) {
		price += priceMap[priceType]?.[6700]?.price || 0;
	}

	return price;
}

export { ComponentPrice, DisplayPricingChoice, getComponentPrice };
